import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useClinicCategories, useAddCategory, useDeleteCategory, getCategoryPolicyCount } from '@/hooks/useClinicCategories';
import { useMyClinicRole } from '@/hooks/useMyClinicRole';
import { Navigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function SettingsCategories() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { data: categories = [], isLoading } = useClinicCategories();
  const addCategory = useAddCategory();
  const deleteCategory = useDeleteCategory();
  const { role, isLoading: roleLoading } = useMyClinicRole();

  const [newName, setNewName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [policyCount, setPolicyCount] = useState(0);
  const [checkingCount, setCheckingCount] = useState(false);

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (role === 'staff') {
    return <Navigate to="/settings/my-profile" replace />;
  }

  const isAdmin = role === 'owner' || role === 'admin';

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (categories.some(c => c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: 'Category already exists', variant: 'destructive' });
      return;
    }
    try {
      await addCategory.mutateAsync(trimmed);
      setNewName('');
      toast({ title: 'Category added' });
    } catch (err: any) {
      toast({ title: 'Failed to add category', description: err.message, variant: 'destructive' });
    }
  };

  const handleDeleteClick = async (cat: { id: string; name: string }) => {
    setCheckingCount(true);
    const count = await getCategoryPolicyCount(cat.name, profile!.clinic_id);
    setPolicyCount(count);
    setDeleteTarget(cat);
    setCheckingCount(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCategory.mutateAsync(deleteTarget.id);
      toast({ title: 'Category deleted' });
    } catch (err: any) {
      toast({ title: 'Failed to delete', description: err.message, variant: 'destructive' });
    }
    setDeleteTarget(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      {/* Section Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Policy Categories</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage the categories available when creating policies
        </p>
      </div>

      {/* Add Category (admin/owner only) */}
      {isAdmin && (
        <div className="flex gap-2 mb-6">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name"
            className="border-gray-200 dark:border-border bg-white dark:bg-background"
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <Button
            onClick={handleAdd}
            disabled={addCategory.isPending || !newName.trim()}
            size="sm"
            className="shrink-0"
          >
            {addCategory.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <><Plus className="h-4 w-4 mr-1" /> Add</>
            )}
          </Button>
        </div>
      )}

      {/* Category List */}
      {categories.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No categories configured yet.</p>
      ) : (
        <ul className="space-y-1">
          {categories.map((cat) => (
            <li
              key={cat.id}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200 dark:border-border bg-white dark:bg-background"
            >
              <span className="text-sm text-foreground">{cat.name}</span>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteClick(cat)}
                  disabled={checkingCount}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {policyCount > 0
                ? `This category is used by ${policyCount} ${policyCount === 1 ? 'policy' : 'policies'}. Deleting it will leave those policies uncategorized.`
                : 'This category is not used by any policies. It will be permanently removed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
